import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Download, Filter, Building2 } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import { adminApi, formatCOP, tokens } from "../../lib/api";

const STATUS_COLORS = {
  recibido: "bg-cm-yellow/30 text-cm-ink border-cm-yellow",
  en_revision: "bg-blue-100 text-blue-800 border-blue-300",
  confirmado: "bg-green-100 text-green-800 border-green-300",
  enviado: "bg-purple-100 text-purple-800 border-purple-300",
  cancelado: "bg-red-100 text-red-800 border-red-300",
};
const STATUS_LABEL = {
  recibido: "Recibido", en_revision: "En revisión", confirmado: "Confirmado",
  enviado: "Enviado", cancelado: "Cancelado",
};

const STATUS_OPTIONS = [
  { v: "all", l: "Todos los estados" },
  { v: "recibido", l: "Recibido" },
  { v: "en_revision", l: "En revisión" },
  { v: "confirmado", l: "Confirmado" },
  { v: "enviado", l: "Enviado" },
  { v: "cancelado", l: "Cancelado" },
];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("all");
  const [sede, setSede] = useState("all");
  const [sedeOptions, setSedeOptions] = useState([]);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (estado && estado !== "all") params.estado = estado;
    if (sede && sede !== "all") params.cliente_id = sede;
    try {
      const { data } = await adminApi.get("/admin/orders", { params });
      setOrders(data);
    } catch (err) {
      setOrders([]);
    } finally { setLoading(false); }
  };

  // Pre-fetch all orders ONCE to build the sede dropdown options (only sedes that have ordered)
  useEffect(() => {
    adminApi.get("/admin/orders", { params: { limit: 500 } })
      .then(({ data }) => {
        const map = new Map();
        (data || []).forEach((o) => {
          if (o.cliente_id) {
            const key = String(o.cliente_id);
            if (!map.has(key)) {
              map.set(key, {
                cliente_id: key,
                label: o.almacen || o.razon_social,
                razon_social: o.razon_social,
                nit: o.nit,
              });
            }
          }
        });
        const opts = Array.from(map.values()).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
        setSedeOptions(opts);
      })
      .catch(() => setSedeOptions([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = {};
      if (q) params.q = q;
      if (estado && estado !== "all") params.estado = estado;
      if (sede && sede !== "all") params.cliente_id = sede;
      try {
        const { data } = await adminApi.get("/admin/orders", { params });
        if (!cancelled) setOrders(data);
      } catch (err) {
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [estado, sede]);

  const onSearchSubmit = (e) => { e.preventDefault(); load(); };

  const downloadCsv = async () => {
    const params = {};
    if (estado && estado !== "all") params.estado = estado;
    if (sede && sede !== "all") params.cliente_id = sede;
    const resp = await adminApi.get("/admin/orders/export", { params, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `pedidos_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Gestión</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-1 tracking-tight">Pedidos</h1>
        </div>
        <Button onClick={downloadCsv} variant="outline" className="border-cm-ink hover:bg-cm-ink hover:text-cm-cream" data-testid="export-csv-btn">
          <Download className="h-4 w-4 mr-2" /> Exportar CSV
        </Button>
      </div>

      <Card className="p-4 bg-white border-cm-border shadow-none">
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={onSearchSubmit} className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cm-ink-soft" />
            <Input
              placeholder="Buscar por NIT, razón social, sede o número de pedido..."
              value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-9 border-cm-border focus-visible:ring-cm-red focus-visible:ring-offset-0"
              data-testid="orders-search-input"
            />
          </form>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className="w-full sm:w-52 border-cm-border" data-testid="status-filter">
              <Filter className="h-4 w-4 mr-2" /> <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v} data-testid={`status-option-${s.v}`}>{s.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sede} onValueChange={setSede}>
            <SelectTrigger className="w-full sm:w-60 border-cm-border" data-testid="sede-filter">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Todas las sedes" />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="all" data-testid="sede-option-all">Todas las sedes</SelectItem>
              {sedeOptions.map((s) => (
                <SelectItem
                  key={s.cliente_id}
                  value={s.cliente_id}
                  data-testid={`sede-option-${s.cliente_id}`}
                >
                  <span className="font-medium">{s.label}</span>
                  <span className="text-cm-ink-soft text-xs ml-2 font-mono">· {s.nit}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="bg-white border-cm-border shadow-none overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-cm-ink-soft">Cargando pedidos...</p>
        ) : orders.length === 0 ? (
          <p className="p-8 text-center text-cm-ink-soft" data-testid="orders-empty">Sin pedidos para los filtros aplicados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="text-left text-xs uppercase tracking-wider text-cm-ink-soft bg-cm-cream-alt/40">
                <tr>
                  <th className="px-4 py-3">Pedido</th>
                  <th className="px-4 py-3">Fecha</th>
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Arcos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cm-border">
                {orders.map((o) => (
                  <tr key={o.order_number} className="hover:bg-cm-cream-alt/30" data-testid={`order-row-${o.order_number}`}>
                    <td className="px-4 py-3">
                      <Link to={`/admin/pedidos/${o.order_number}`} className="font-mono text-cm-red hover:underline">{o.order_number}</Link>
                    </td>
                    <td className="px-4 py-3 text-cm-ink-soft text-xs whitespace-nowrap">{new Date(o.created_at).toLocaleString("es-CO")}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium truncate max-w-[220px]">{o.razon_social}</div>
                      {o.almacen && o.almacen !== o.razon_social && (
                        <div className="text-[11px] text-cm-ink truncate max-w-[220px] flex items-center gap-1">
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-cm-yellow flex-shrink-0" />
                          <span className="truncate">Sede: {o.almacen}</span>
                        </div>
                      )}
                      <div className="text-xs font-mono text-cm-ink-soft">{o.nit} · {o.lista_precios_nombre}</div>
                    </td>
                    <td className="px-4 py-3 font-mono">{o.lineas?.length || 0}</td>
                    <td className="px-4 py-3 font-mono font-semibold">{formatCOP(o.total)}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={STATUS_COLORS[o.estado]}>{STATUS_LABEL[o.estado] || o.estado}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono uppercase tracking-wider ${o.arcos_sync_status === "synced" ? "text-green-700" : "text-cm-ink-soft"}`}>
                        {o.arcos_sync_status === "synced" ? "● Sincronizado" : "○ Local"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
